import { z } from 'zod';
import { FORM_FIELD_TYPES, FILTER_OPERATORS, LEAD_VARIABLES } from './constants.js';
/**
 * Schema describing a single field in a DynamicForm. Connector config UIs and
 * the integration wizard's destination step are rendered from arrays of these.
 *
 * Note: zod can't easily express the recursive `fields`/`itemFields` here, so we
 * type those loosely (`z.array(z.any())`) and rely on the exported TS type below.
 */
export const formFieldSchema = z.lazy(() => z.object({
    name: z.string(),
    type: z.enum(FORM_FIELD_TYPES),
    label: z.string().optional(),
    description: z.string().optional(),
    placeholder: z.string().optional(),
    required: z.boolean().optional(),
    default: z.unknown().optional(),
    /** select / multiselect static options */
    options: z.array(z.object({ label: z.string(), value: z.string() })).optional(),
    /** async-select: tRPC `apps.loadOptions` is called with this key */
    optionsSource: z.string().optional(),
    /** connection-picker: which connection type to pick */
    connectionType: z.enum(['facebook', 'telegram', 'google', 'webhook']).optional(),
    /** code field language hint */
    language: z.enum(['json', 'text', 'handlebars']).optional(),
    /** group: nested fields */
    fields: z.array(formFieldSchema).optional(),
    /** repeatable: fields for each row */
    itemFields: z.array(formFieldSchema).optional(),
    /** show this field only when another field has a given value */
    showIf: z.object({ field: z.string(), equals: z.unknown() }).optional(),
    /** allow toggling a literal value vs a `{{...}}` mapped expression */
    mappable: z.boolean().optional(),
}));
export const formSchemaSchema = z.array(formFieldSchema);
/** A single rule in a filter / condition builder. */
export const filterRuleSchema = z.object({
    field: z.string(), // e.g. "lead.email" or a custom variable / form field
    operator: z.enum(FILTER_OPERATORS),
    value: z.string().optional(),
});
/** AND of rules; the destination/condition passes if all rules match. */
export const filterGroupSchema = z.object({
    match: z.enum(['all', 'any']).default('all'),
    rules: z.array(filterRuleSchema).default([]),
});
/**
 * Field-mapping value: a destination field is filled from either a Facebook lead
 * form field, a lead-metadata variable, or a static value (which itself may
 * contain `{{...}}` placeholders).
 */
export const fieldMappingEntrySchema = z.object({
    /** destination-side key being filled */
    to: z.string(),
    /** "form_field" | "lead_var" | "static" */
    source: z.enum(['form_field', 'lead_var', 'static']),
    /** form field name, lead variable, or the static template */
    value: z.string(),
});
export const fieldMappingSchema = z.array(fieldMappingEntrySchema);
export const LEAD_VARIABLE_SET = new Set(LEAD_VARIABLES);
//# sourceMappingURL=forms.js.map